/**
 * Plan card component for billing page.
 */

import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/utils/cn';
import './plan-card.scss';

export interface PlanFeature {
  text: string;
  included: boolean;
}

export interface PlanCardProps {
  id: string;
  name: string;
  price: number;
  features: PlanFeature[];
  isCurrentPlan?: boolean;
  isPopular?: boolean;
  hasTrial?: boolean;
  trialDays?: number;
  onUpgrade?: () => void;
  isLoading?: boolean;
  isContactSales?: boolean;
}

export function PlanCard({
  id,
  name,
  price,
  features,
  isCurrentPlan = false,
  isPopular = false,
  hasTrial = false,
  trialDays = 14,
  onUpgrade,
  isLoading = false,
  isContactSales = false,
}: PlanCardProps) {
  const handleClick = () => {
    if (isContactSales) {
      // Open email client or contact form
      window.location.href = 'mailto:sales@origyns.io?subject=Scale Plan Inquiry';
      return;
    }
    if (onUpgrade && !isCurrentPlan) {
      onUpgrade();
    }
  };

  return (
    <Card
      className={cn('plan-card', {
        'plan-card--current': isCurrentPlan,
        'plan-card--popular': isPopular,
      })}
    >
      {isPopular && (
        <div className="plan-card__badge">Most Popular</div>
      )}
      {isCurrentPlan && (
        <div className="plan-card__badge plan-card__badge--current">Current Plan</div>
      )}
      
      <div className="plan-card__header">
        <h3 className="plan-card__name">{name}</h3>
        <div className="plan-card__price">
          {price === 0 ? (
            <span className="plan-card__price-amount">Free</span>
          ) : (
            <>
              <span className="plan-card__price-amount">${price}</span>
              <span className="plan-card__price-period">/mo</span>
            </>
          )}
        </div>
        {hasTrial && (
          <div className="plan-card__trial">
            {trialDays}-day free trial
          </div>
        )}
      </div>

      <ul className="plan-card__features">
        {features.map((feature, index) => (
          <li
            key={index}
            className={cn('plan-card__feature', {
              'plan-card__feature--included': feature.included,
              'plan-card__feature--excluded': !feature.included,
            })}
          >
            {feature.included ? '✅' : '❌'} {feature.text}
          </li>
        ))}
      </ul>

      <div className="plan-card__footer">
        {isContactSales ? (
          <Button
            variant="primary"
            onClick={handleClick}
            className="plan-card__button"
          >
            Contact Sales
          </Button>
        ) : (
          <Button
            variant={isCurrentPlan ? 'secondary' : 'primary'}
            onClick={handleClick}
            disabled={isCurrentPlan || isLoading}
            className="plan-card__button"
          >
            {isCurrentPlan
              ? 'Current Plan'
              : isLoading
              ? 'Loading...'
              : `Upgrade to ${name}`}
          </Button>
        )}
      </div>
    </Card>
  );
}

